import { nanoid } from "nanoid";
import { IEventEmitter, IEventSource } from "../lib/eventing/events";
import { WorkbookClassificationSource } from "./workbookClassification";
import { ProgressiveEnhancementSource } from "./progressiveEnhancement";

type Writable<T> = { -readonly [P in keyof T]: T[P] };



export type RuleAlert<TContext extends {} = {}> = {
    readonly code:string;
    readonly context?:TContext;
}
export type RuleAlertInfo = {
    readonly level:"error"|"warning"|"info";
    readonly title:string;
    readonly message:string;
}
export type RuleAlertMapping = {
    readonly outputCells:string[];
    readonly highlightRanges:string[];
    readonly focusRange:string;
}

export type AlertDismissal<TContext extends {} = {}> = {
    readonly id:string;
    readonly timestamp:number
    readonly reasonCode:string;
    readonly context:TContext;
}

export type AlertState = "active" | "not-applicable" | "resolved" | "dismissed";

export type AlertEntry<TContext extends {} = {}> = RuleAlert<TContext> & {
    readonly timestamp:number
    readonly id:string;
    readonly ruleId:string;
    readonly sheetId:string;
    readonly state: AlertState;

    readonly info: RuleAlertInfo;
    readonly mapping: RuleAlertMapping;
    
    readonly dismissal?: AlertDismissal<TContext>;
    //TODO: resolution? eg. NA (rule no longer applicable) or resolved (rule no longer raises this alert)
}

export type AlertEvents = {
    readonly alertRaised: AlertEntry,
    readonly alertResolved: AlertEntry,
    readonly alertDismissed: AlertEntry & { reason: { }},
    readonly alertRestored: AlertEntry
}

export type AlertEmitter = IEventEmitter<AlertEvents>;
export type AlertSource = IEventSource<AlertEvents>;

export type RuleContext = {
    readonly workbookId: string;
    readonly workbookCode: string;
    readonly worksheetId: string;
    readonly worksheetCode: string;
    readonly ruleId: string;
    readonly inputs: Record<string, unknown>;
    raiseAlert(alert:RuleAlert):AlertEntry;
}
export type RuleApplicabilityContext = Omit<RuleContext, "raiseAlert">;

export type RuleDefinition = {
    readonly ruleId:string;
    readonly workbookCode:string; // applies any workbook of this code
    readonly worksheetCode:string; // applies to every worksheet of this code
    //TODO: other context reference/qualifiers
    readonly inputs: { //TODO: strongly type inputs to have them passed in on contexts?
        readonly range:string; // range is relative for each worksheet of the matching code. Eg. three separate rental worksheets could have this rule evaluated once in each.
        readonly required?:boolean; // if required and missing, rule is not considered applicable
    }[],
    readonly outputCells:string[];
    applicable(inputs:RuleApplicabilityContext):boolean;
    run(context:RuleContext):void;
}

export type SemanticVersion<TMajor extends number = number, TMinor extends number = number, TPatch extends number = number> = {
    readonly major: TMajor;
    readonly minor: TMinor;
    readonly patch: TPatch;
}
export type WorkbookRuleset<TWorkbookCode extends string = string, TVersion extends SemanticVersion = SemanticVersion> = {
    readonly workbookCode:TWorkbookCode;
    readonly version:TVersion;
    readonly name:string;
    readonly description:string;
    readonly alertCodes: Record<string, (context:unknown)=>RuleAlertInfo>;
    readonly worksheetRules: Record<string, RuleDefinition[]>;
}

export type WorkbookRulesetFetcher = (workbookCode:string, version?:string) => Promise<WorkbookRuleset|undefined>;

const reraiseAlertStates:AlertState[] = ["not-applicable", "resolved"];

export class WorkbookAnalyticsRulesEngine {
    private readonly alertsBySheetIdRuleIdAndAlertCode:Record<string, Record<string, Record<string, Writable<AlertEntry>>>> = {};
    private readonly alertsById:Record<string, Writable<AlertEntry>> = {};
    private readonly activeRulesets:Record<string,string> = {}
    private ruleSet?: Promise<WorkbookRuleset>;
    private workbookId?: string;

    constructor(
        private readonly rulesetFetcher: WorkbookRulesetFetcher,
        private readonly workbookClassificationEvents:WorkbookClassificationSource,
        private readonly progressiveEnhancementEvents:ProgressiveEnhancementSource,
        private readonly alertEventEmitter:AlertEmitter
    ) {
        this.workbookClassificationEvents.events.workbookClassified.on(async (payload) => this.handleWorkbookClassified(payload.code, payload.workbookId));
        this.progressiveEnhancementEvents.events.worksheetProcessed.on(async (payload) => this.handleSheetClassified(payload.worksheetCode, payload.worksheetId));
    }

    private async handleWorkbookClassified(workbookCode:string, workbookId:string) {
        const ruleSet = await this.loadRuleSet(workbookId, workbookCode);
        if (!ruleSet) {
            console.warn(`No ruleset found for workbook code ${workbookCode}`);
        }
    }
    private async handleSheetClassified(worksheetCode:string, worksheetId:string) {
        if (!this.ruleSet) return;
        await this.applyRulesToSheet(worksheetId, worksheetCode);
    }

    public get alerts(): ReadonlyArray<AlertEntry> { 
        return Object.values(this.alertsById);
    }

    public dismissAlert(alertId: string, reasonCode: string = "user_dismissed", context?: unknown) {
        const alert = this.alertsById[alertId];
        if (!alert) return;
        const dismissal: AlertDismissal = {
            id: nanoid(),
            timestamp: Date.now(),
            reasonCode,
            context: context || {}
        };

        alert.dismissal = dismissal;
        this.alertEventEmitter.emit('alertDismissed', { 
            ...alert, 
            reason: dismissal 
        });
    }

    public restoreAlert(alertId: string) {
        const alert = this.alertsById[alertId];
        if (!alert) return;
        const wasDeleted = delete alert.dismissal;
        if (!wasDeleted) return;
        this.alertEventEmitter.emit('alertRestored', alert);
    }

    private async loadRuleSet(workbookId: string, workbookCode:string) {
        if (this.ruleSet) return undefined;

        //TODO: if we have a stored ruleset for this workbookId then fetch that instead

        this.workbookId = workbookId;
        this.ruleSet = this.rulesetFetcher(workbookCode);

        //TODO: store the ruleset details on the workbook

        return await this.ruleSet;
    }

    private async applyRulesToSheet(worksheetId:string, worksheetCode: string) {
        if (!this.ruleSet) return;
        const ruleSet = await this.ruleSet;
        if (this.activeRulesets[worksheetId]) return;
        this.activeRulesets[worksheetId] = worksheetCode;
        const worksheetRules = ruleSet.worksheetRules[worksheetCode];

        if (!worksheetRules || worksheetRules.length === 0) {
            console.warn(`No rules found for worksheet code ${worksheetCode}`);
            return;
        }

        this.createBindings(worksheetRules, worksheetId);

        //TODO: emit ruleset loaded?

        //TODO: run rules now
        for (const rule of worksheetRules) void this.queueRule(worksheetId, rule);
    }

    private createBindings(worksheetRules: RuleDefinition[], worksheetId: string) {
        const inputRangeToRules: Record<string, RuleDefinition[]> = {};
        for (const rule of Object.values(worksheetRules)) {
            for (const input of rule.inputs) {
                inputRangeToRules[input.range] ??= [];
                inputRangeToRules[input.range].push(rule);
            }
        }

        //TODO: consider pushing this out into the excel relay
        Excel.run(async (context) => {
            const workbook = context.workbook;
            const bindings = context.workbook.bindings;
            const worksheet = workbook.worksheets.getItem(worksheetId);

            for (const range in inputRangeToRules) {
                const binding = bindings.add(worksheet.getRange(range), Excel.BindingType.range, range);
                binding.onDataChanged.add(async () => {
                    for (const rule of inputRangeToRules[range]) {
                        console.log("Queuing rule", rule.ruleId);
                        void this.queueRule(worksheetId, rule);
                    }
                });
            }
            await context.sync();
        });
    }

    private async queueRule(worksheetId:string, rule: RuleDefinition) {
        if (!this.ruleSet) return;
        const ruleSet = await this.ruleSet;

        //TODO: in the future enqueue this to be debounced and processed in waves
        Excel.run(async (ctx) => {
            const workbook = ctx.workbook;
            const worksheet = workbook.worksheets.getItem(worksheetId);
            const inputs: Record<string, unknown> = {};
            const ranges: Record<string, Excel.Range> = {};

            // Build inputs by reading ranges from Excel
            for (const input of rule.inputs) {
                const range = worksheet.getRange(input.range);
                range.load('values');
                ranges[input.range] = range;
            }
            await ctx.sync();

            for (const input of rule.inputs) {
                inputs[input.range] = ranges[input.range].values;
            }

            const applicabilityContext: RuleApplicabilityContext = {
                workbookId:this.workbookId,
                workbookCode: rule.workbookCode,
                worksheetId,
                worksheetCode: rule.worksheetCode,
                ruleId: rule.ruleId,
                inputs
            }

            // Check if rule is applicable
            const applicable = rule.applicable(applicabilityContext);
            if (!applicable) {
                // Clear all alerts for this rule
                const sheetRuleAlerts = Object.values(this.alertsBySheetIdRuleIdAndAlertCode[worksheetId]?.[rule.ruleId] ?? {});
                for (const alert of sheetRuleAlerts) {
                    alert.state = "not-applicable";
                    this.alertEventEmitter.emit('alertResolved', alert);
                }
                delete this.alertsBySheetIdRuleIdAndAlertCode[rule.ruleId];
                return;
            }

            const expectExistingAlerts = {... this.alertsBySheetIdRuleIdAndAlertCode[worksheetId]?.[rule.ruleId]};
            const ruleContext:RuleContext = {
                ...applicabilityContext,
                raiseAlert: (alert: RuleAlert): AlertEntry => {
                    const alertEntryContext = {... alert.context, inputs};
                    const alertEntry: AlertEntry = {
                        ...alert,
                        timestamp: Date.now(),
                        id: nanoid(),
                        ruleId: rule.ruleId,
                        sheetId: worksheetId,
                        state: "active",
                        context: {... alert.context, inputs},
                        info: ruleSet.alertCodes[alert.code](alertEntryContext),
                        mapping: {
                            focusRange: rule.outputCells[0],
                            highlightRanges: rule.inputs.map(input => input.range), //TODO: needs to be the mapped ranges
                            outputCells: rule.outputCells
                        }
                    };

                    const existingAlert = expectExistingAlerts[alert.code];
                    if (existingAlert) {
                        const existingAlertState = existingAlert.state;
                        existingAlert.context = rule.inputs;
                        existingAlert

                        if (reraiseAlertStates.includes(existingAlertState)) {
                            existingAlert.state = "active";
                            this.alertEventEmitter.emit('alertRaised', existingAlert);
                        }
                        delete expectExistingAlerts[alert.code];
                        return existingAlert;
                    }
                    this.alertsById[alertEntry.id] = alertEntry;
                    this.alertsBySheetIdRuleIdAndAlertCode[worksheetId] ??= {};
                    this.alertsBySheetIdRuleIdAndAlertCode[worksheetId][rule.ruleId] ??= {};
                    this.alertsBySheetIdRuleIdAndAlertCode[worksheetId][rule.ruleId][alert.code] = alertEntry;

                    this.alertEventEmitter.emit('alertRaised', alertEntry);

                    return alertEntry;
                }
            };

            // Run the rule and get new alerts
            rule.run(ruleContext);

            // Emit events for resolved alerts (codes that no longer appear)
            const staleAlerts = Object.values(expectExistingAlerts);
            for (const alert of staleAlerts) {
                alert.state = "resolved";
                //TODO: add resolution details?
                this.alertEventEmitter.emit('alertResolved', alert);
            }
        });
    }
}